import {MachineId, MachinePoolId} from '../ids';
import {isDefined} from "@webmate-web-core/shared/utils/utils";
import {
    DeviceProperties,
    DevicePropertyName,
    DevicePropertyNames
} from "@webmate-web-core/shared/types/devices/device-properties";
import {Browser, browserFromBackendObject, browserSortingFunction} from "@webmate-web-core/shared/types/browser";

export class Machine {

    private browsers?: Array<Browser>;

    constructor(public readonly id: MachineId,
                public readonly state: string,
                public readonly properties: DeviceProperties,
                public readonly assignedPool: MachinePoolId,
                public autoUpdate: boolean, public annotations?: any, public userAnnotations?: any) {
    }

    getModel(): string {
        return this.properties[DevicePropertyNames.ModelName] ?? "unknown";
    }

    getManufacturer(): string {
        return this.properties[DevicePropertyNames.ManufacturerName] ?? "unknown";
    }

    getBrowsers(): Array<Browser> {
        if (!this.browsers) {
            this.browsers = (this.properties[DevicePropertyNames.MachineBrowsers] as any[]).map(browserFromBackendObject).filter(x => !!x).map(b => b as Browser).sort(browserSortingFunction);
        }
        return this.browsers;
    }
}

export interface MachinePool {
    id: MachinePoolId,
    name: string,
    machines: Array<Machine>,
    autoUpdate: boolean
}

export class MachinePoolUpdateConfig {
    constructor(public readonly autoUpdateDefault: boolean,
                public readonly machineSpecificSettings: { [m: string]: boolean }) {
    }
}


export function machineFromBackendObject(m: any): Machine {
    return new Machine(m.id, m.state, m["properties"] as DeviceProperties, m.assignedPool, m.autoUpdate, m.annotations, m.userAnnotations);
}

export function machinePoolFromBackendObject(m: any): MachinePool {
    return {
        ...m,
        machines: m["machines"].map(machineFromBackendObject).sort(machineSortingFunction)
    };
}

export function machineSortingFunction(a: Machine, b: Machine): number {
    const modelA = a.properties[DevicePropertyNames.ModelName];
    const modelB = b.properties[DevicePropertyNames.ModelName];
    if (isDefined(modelA) && isDefined(modelB)) {

        //separate different types of platforms
        const platformTypeA = a.properties[DevicePropertyNames.MachinePlatformType];
        const platformTypeB = b.properties[DevicePropertyNames.MachinePlatformType];
        if (platformTypeA !== platformTypeB) {
            return platformTypeA.localeCompare(platformTypeB)
        }

        //sort by manufacturer
        const manufacturerA = a.properties[DevicePropertyNames.ManufacturerName];
        const manufacturerB = b.properties[DevicePropertyNames.ManufacturerName];
        if (isDefined(manufacturerA) && isDefined(manufacturerB) && manufacturerA !== manufacturerB) {
            return manufacturerA.localeCompare(manufacturerB);
        }

        //sort by model
        if (modelA !== modelB) {
            return modelA.localeCompare(modelB);
        }

        //sort by platform for identical models
        const platformA = a.properties[DevicePropertyNames.MachinePlatform];
        const platformB = b.properties[DevicePropertyNames.MachinePlatform];
        return platformA.localeCompare(platformB);
    } else {
        return a.id.localeCompare(b.id, undefined,{ numeric: true });
    }
}
